<template>
  <div
    class="oct-card"
    :class="{
      'oct-card--with-bullet': bullet,
      'oct-card--with-author': author.href && author.text,
      'oct-card--with-action': action,
      'oct-card--square': square,
      'oct-card--reverse': reverse,
      'oct-card--dense': dense,
      'oct-card--done': done,
    }"
  >
    <div class="oct-card__content">
      <component
        :is="href ? 'a' : 'div'"
        class="oct-card__thumbnail"
        :href="href"
      >
        <!-- @event 'remove' -->
        <oct-media
          v-bind="media"
          :removable="removable"
          @remove="$emit('remove')"
          v-if="!Array.isArray(media)"
        />

        <div
          class="oct-card__media-list"
          v-else
        >
          <oct-media
            class="oct-card-media"
            natural
            v-for="(item, i) in media"
            :key="i"
            v-bind="item"
          />
        </div>

        <oct-icon-button
          :icon="$data.$_octCard_done ? 'done' : 'add'"
          size="dense"
          v-if="action"
          @click.native="onAction"
          @mouseenter.native="onMouseEnter"
          @mouseleave.native="onMouseLeave"
        />
      </component>

      <div class="oct-card__text">
        <component
          :is="href ? 'a' : 'div'"
          :href="href ? href : null"
          class="oct-card__title"
        >
          <span class="oct-card__title-text">{{ title }}</span>
          <h2 class="oct-card__primary-text">{{ headline }}</h2>
          <p class="oct-card__subtitle-text">{{ subtitle }}</p>
        </component>

        <component
          :is="href || supportingHref ? 'a' : 'div'"
          :href="supportingHref ? supportingHref : href"
          class="oct-card__supporting-text"
        >
          <p class="oct-card__secondary-text" v-if="text">{{ text }}</p>
          <p class="oct-card__caption-text" v-if="caption">{{ caption }}</p>
        </component>

        <a
          :href="author.href"
          class="oct-card__author"
          v-if="author.href && author.text"
        >
          <oct-media
            v-bind="author.media"
          />

          <p class="oct-card__author-text">{{ author.text }}</p>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import OctMedia from '@/components/media/OctMedia.vue';
import OctIconButton from '@/components/icon-button/OctIconButton.vue';

export default {
  name: 'OctCard',
  components: {
    OctMedia,
    OctIconButton
  },
  props: {
    /** Card visual */
    media: {
      type: [Object,Array],
      default () {
        return {
          image: {
            lowResolution: '',
            standardResolution: '',
          }
        }
      }
    },
    /** Title text */
    title: {
      type: String,
      default: ''
    },
    /** Headline text */
    headline: {
      type: String,
      default: ''
    },
    /** Sub title text */
    subtitle: {
      type: String,
      default: ''
    },
    /** Text under the headline */
    text: {
      type: String,
      default: ''
    },
    /** Caption text */
    caption: {
      type: String,
      default: ''
    },
    /** Author */
    author: {
      type: Object,
      default () {
        return {
          media: {
            image: {
              lowResolution: '',
              standardResolution: '',
            }
          },
          text: '',
          href: ''
        }
      }
    },
    /** Link */
    href: {
      type: String,
      default: ''
    },
    /** Supporting Link */
    supportingHref: {
      type: String,
      default: ''
    },
    /** Bullet type [default|primary|secondary] */
    bullet: {
      type: String,
      default: ''
    },
    /** Action */
    action: {
      type: Boolean,
      default: false
    },
    /** Action is done */
    done: {
      type: Boolean,
      default: false
    },
    /** Square layout */
    square: {
      type: Boolean,
      default: false
    },
    /** Reverse layout */
    reverse: {
      type: Boolean,
      default: false
    },
    /** Dense */
    dense: {
      type: Boolean,
      default: false
    },
    /** Removable */
    removable: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      $_octCard_done: this.done
    }
  },
  methods: {
    /** Action button click handler */
    onAction (event) {
      // Kill default event
      event.preventDefault()
      event.stopPropagation()

      /** @event action */
      this.$emit('action')
    },
    /** Action button mouseenter handler */
    onMouseEnter () {
      // Swap icon
      if (this.done) this.$data.$_octCard_done = false
    },
    /** Action button mouseleave handler */
    onMouseLeave () {
      // Reset icon
      this.$data.$_octCard_done = this.done
    },
  }
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-card {
@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
}
}

.oct-card__content {
  -webkit-tap-highlight-color: rgba(0,0,0,0);
}

.oct-card__thumbnail {
  position: relative;
  margin: 0 0 oct-rem(16);
  display: block;

  &::v-deep .oct-icon-button:not(.oct-media__icon--lock) {
    position: absolute;
    right: oct-rem(8);
    bottom: oct-rem(8);
    transform: scale(.75);
    transform-origin: right bottom;
    background-color: rgba(255,255,255,.85);
    z-index: 1;
  }

  .oct-card--done &::v-deep .oct-icon-button:not(.oct-media__icon--lock) {
    background-color: $oct-theme--primary;

    .oct-icon {
      stroke: $oct-theme--on-primary;
    }

    &:hover {
      background-color: transparent;

      .oct-icon {
        stroke: $oct-theme--primary;
      }
    }
  }

  .oct-card--square & {
    .oct-media {
      height: auto;
      padding-top: (212%/282)*100;
    }

    .oct-media__picture {
      position: absolute;
    }
  }

  .oct-card--reverse.oct-card--with-author & {
    margin-bottom: oct-rem(8);
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin-bottom: oct-rem(20);

  .oct-card--dense.oct-card--with-author & {
    margin-bottom: oct-rem(16);
  }

  .oct-card--reverse.oct-card--with-author & {
    margin-bottom: oct-rem(12);
  }

  &::v-deep .oct-icon-button:not(.oct-media__icon--lock) {
    top: 100%;
    right: 0;
    bottom: auto;
    margin-top: oct-rem(10);
    background-color: transparent;
    border-color: $oct-theme--primary;
    transform: scale(.7);
    transform-origin: right top;

    &:hover {
      background-color: $oct-theme--primary;

      .oct-icon {
        stroke: $oct-theme--on-primary;
      }
    }
  }

  .oct-card--done &::v-deep .oct-icon-button:not(.oct-media__icon--lock) {
    &:hover {
      .oct-icon {
        transform: rotate(45deg);
      }
    }
  }

  .oct-card--reverse &::v-deep .oct-icon-button:not(.oct-media__icon--lock) {
    margin-top: oct-rem(15);
  }

  .oct-card--with-bullet &::v-deep .oct-icon-button:not(.oct-media__icon--lock) {
    margin-top: oct-rem(43);
  }
}
}

.oct-card__media-list {
  border-radius: oct-rem(8);
  overflow: hidden;

  &:hover {
    .oct-card-media:after {
      opacity: .05;
    }
  }

  .oct-card-media {
    margin-bottom: oct-rem(2);
    border-radius: 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.oct-card-media {
  margin: 0;
  padding: 0;
  border-radius: oct-rem(8);
  overflow: hidden;
}

.oct-card-media__picture,
.oct-card-media__image {
  position: static;
  display: block;
  width: 100%;
}

.oct-card__text {
  color: $oct-theme--primary;
  text-decoration: none;
  display: flex;
  flex-direction: column;

  &:hover {
    text-decoration: none;
  }

  .oct-card--reverse & {
    flex-direction: column-reverse;
  }

  .oct-card--column & {
    flex-direction: row;
  }
}

.oct-card__title {
  text-decoration: none;
  color: inherit;
  display: block;

  .oct-card--column & {
    flex: 0 0 50%;
    display: flex;
    padding: 0 oct-rem(6) 0 0;
    flex-direction: column-reverse;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  .oct-card--with-action & {
    padding-right: oct-rem(30);
  }
}
}

.oct-card__supporting-text {
  color: inherit;
  text-decoration: none;
  display: block;
  
  .oct-card--column & {
    display: none;
    /*
    flex: 0 0 50%;
    display: flex;
    padding: 3px 0 0 6px;
    */
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  .oct-card--with-action & {
    padding-right: oct-rem(30);
  }
}
}


.oct-card__title-text {
  font-family: $font-family-sans-serif;
  font-size: oct-rem(11);
  font-weight: normal;
  line-height: 1;
  margin: (-1em/11) 0 ((-1em+14)/11);
  display: block;
  opacity: .55;

  &:empty {
    display: none;
  }

  .oct-card--column & {
    margin-bottom: (-1em/11);
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(13);
  font-weight: normal;
  line-height: (18/13);
  margin: (-4em/13) 0 ((-4em+16)/13);
}
}

.oct-card__primary-text {
  font-size: oct-rem(21);
  font-weight: normal;
  line-height: (26/21);
  letter-spacing: -.01em;
  word-break: break-all;
  margin: (-5em/21) 0 ((-5em+14)/21);
  display: -webkit-box;
  white-space: pre-line;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;

  &--multi-byte {
    font-size: oct-rem(19);
    line-height: (26/19);
    margin: (-5em/19) 0 ((-5em+14)/19);
  }

  .oct-card--dense & {
    font-size: oct-rem(14);
    font-weight: normal;
    line-height: (17/14);
    letter-spacing: -.005em;
    margin: (-3em/14) 0 ((-3em+8)/14);

    &--multi-byte {
      font-size: oct-rem(13);
      line-height: (17/13);
      letter-spacing: 0;
      margin: (-3em/13) 0 ((-3em+8)/13);
    }
  }

  .oct-card--column & {
    font-size: oct-rem(18);
    line-height: (22/18);
    letter-spacing: -.005em;
    margin: (-4em/18) 0 ((-4em+10)/18);

    &--multi-byte {
      font-size: oct-rem(17);
      line-height: (22/17);
      letter-spacing: 0;
      margin: (-4em/17) 0 ((-4em+10)/17);
    }
  }
 
  &:empty {
    display: none;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  &, .oct-card--dense & {
    font-size: oct-rem(23);
    font-weight: normal;
    line-height: (30/23);
    letter-spacing: 0;
    margin: (-6em/23) 0 ((-6em+16)/23);

    &--multi-byte {
      font-size: oct-rem(21);
      line-height: (30/21);
      letter-spacing: 0;
      margin: (-6em/21) 0 ((-6em+16)/21);
    }
  }

  .oct-card--dense.oct-card--with-author & {
    font-size: oct-rem(18);
    line-height: (24/18);
    letter-spacing: 0;
    margin: (-5em/18) 0 ((-5em+10)/18);

    &--multi-byte {
      font-size: oct-rem(17);
      line-height: (24/17);
      letter-spacing: .01em;
      margin: (-5em/17) 0 ((-5em+10)/17);
    }
  }

  .oct-card--column & {
    font-size: oct-rem(21);
    line-height: (26/21);
    letter-spacing: 0;
    margin: (-5em/21) 0 ((-5em+12)/21);

    &--multi-byte {
      font-size: oct-rem(19);
      line-height: (26/19);
      letter-spacing: 0;
      margin: (-5em/19) 0 ((-5em+12)/19);
    }
  }
}
}

.oct-card__subtitle-text {
  font-family: $font-family-sans-serif;
  font-size: oct-rem(21);
  font-weight: normal;
  line-height: (24/21);
  letter-spacing: -.001em;
  word-break: break-all;
  margin: (-4em/21) 0 ((-4em+14)/21);

  .oct-card--dense & {
    font-size: oct-rem(13);
    font-weight: normal;
    line-height: (17/13);
    letter-spacing: -.001em;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: (-3em/13) 0 ((-3em+8)/13);
    overflow: hidden;
  }
 
  &:empty {
    display: none;
  } 

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  &, .oct-card--dense & {
    font-size: oct-rem(23);
    line-height: (30/23);
    letter-spacing: -.001em;
    margin: ((-6em-4)/23) 0 ((-6em+16)/23);
  }
}
}

.oct-card__bullet {
  position: absolute;
  top: 0;
  right: oct-rem(4);
  width: oct-rem(8);
  height: oct-rem(8);

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  right: oct-rem(7.5);
  width: oct-rem(10);
  height: oct-rem(10);
}
}

.oct-card__secondary-text {
  font-family: $font-family-sans-serif;
  font-size: oct-rem(11);
  line-height: (15/11);
  margin: (-2em/11) 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
 
  &:empty {
    display: none;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(13);
  line-height: (18/13);
  margin: (-4em/13) 0;
}
}

.oct-card__caption-text {
  font-family: $font-family-sans-serif;
  font-size: oct-rem(11);
  line-height: (15/11);
  white-space: pre-line;
  margin: (-2em/11) 0 ((-2em+7)/11);
  position: relative;
  opacity: .55;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;

  .oct-card--reverse & {
    margin-bottom: (-2em/11);
    display: block;
    overflow: auto;
    opacity: 1;

    &:after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: oct-rem(15);
      left: 0;
      min-height: oct-rem(16);
      background-color: $oct-theme--surface;
      opacity: .45;
    }
  }

  .oct-card--column & {
    margin-bottom: (-2em/11);
  }
 
  &:empty {
    display: none;
  }

  .oct-darkmode--activated .oct-card--reverse & {
    &:after {
      bottom: 0;
      background-color: #181818;
      opacity: .6;
    }
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(13);
  line-height: (18/13);
  margin: (-4em/13) 0 ((-4em+10)/13);
}
}

.oct-card__author {
  color: inherit;
  text-decoration: none;
  display: flex;
  align-items: center;

  .oct-card--reverse & {
    margin: 0 0 8px;
  }

  .oct-media {
    margin: 0 6px 0 0;
    flex: 0 0 20px;
    width: 20px;
    padding-top: 20px;
    border-radius: 50%;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  .oct-card--with-action & {
    padding-right: oct-rem(44);
  }

  .oct-media {
    margin-right: 8px;
    flex: 0 0 32px;
    width: 32px;
    padding-top: 32px;
  }
}
}

.oct-card__author-text {
  font-size: oct-rem(11);
  line-height: (15/11);
  letter-spacing: -.005em;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: (-2em/11) 0;
  display: block;
  overflow: hidden;
  transition-duration: .2s;

  &--multi-byte {
    letter-spacing: 0;
  }

  .oct-card--reverse & {
    font-size: oct-rem(14);
  }
  .oct-card__author:hover & {
    opacity: .6;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(15);
  line-height: (20/15);
  letter-spacing: 0;
  margin: (-4em/15) 0;
 
  .oct-card--reverse & {
    font-size: oct-rem(18);
  }

  &--multi-byte {
    .oct-card--reverse & {
      font-size: oct-rem(17);
      letter-spacing: .01em;
    }
  }
}
}

.oct-card-list__action {
  margin: oct-rem(-40) 0 oct-rem(8);
  display: flex;
  justify-content: flex-end;

  .oct-icon-button {
    background-color: transparent;
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin: oct-rem(-48) 0 oct-rem(12);
}
}
</style>
